import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import {
  InviteWithRelations,
  useGetPropertyByPropertyIdInvitesVisitsQuery,
  usePatchInviteByIdRevokeMutation,
} from '../../../redux/residentPortalApi';
import {usePatchInviteByIdResendMutation} from '../../../redux/inviteResendApi';
import config from '../../../config';

interface OwnProps {
  propertyId: string;
}

const formatVisitWindow = (invite: InviteWithRelations): string => {
  if (invite.isMultiDay) {
    const start = invite.startDate
      ? new Date(invite.startDate).toLocaleDateString('en-GB')
      : 'N/A';
    const end = invite.endDate
      ? new Date(invite.endDate).toLocaleDateString('en-GB')
      : 'N/A';
    return `${start} to ${end}`;
  }

  if (!invite.visitDateTime) {
    return 'N/A';
  }

  return new Date(invite.visitDateTime).toLocaleString('en-GB');
};

const getFilterDate = (invite: InviteWithRelations): Date | undefined => {
  if (!invite.isMultiDay && invite.visitDateTime) {
    return new Date(invite.visitDateTime);
  }

  if (invite.isMultiDay && invite.startDate) {
    return new Date(invite.startDate);
  }

  if (invite.scannedAt) {
    return new Date(invite.scannedAt);
  }

  return undefined;
};

const VisitsTabContent: React.FC<OwnProps> = ({propertyId}) => {
  const {data, isLoading, error, refetch, isFetching} =
    useGetPropertyByPropertyIdInvitesVisitsQuery(
      {propertyId},
      {skip: !propertyId},
    );
  const [revokeInvite, {isLoading: isRevoking}] =
    usePatchInviteByIdRevokeMutation();
  const [resendInvite, {isLoading: isResending}] =
    usePatchInviteByIdResendMutation();
  const [visitorTypeFilter, setVisitorTypeFilter] = React.useState('all');
  const [startDateFilter, setStartDateFilter] = React.useState('');
  const [endDateFilter, setEndDateFilter] = React.useState('');
  const [checkedInOnly, setCheckedInOnly] = React.useState(false);
  const [revokedOnly, setRevokedOnly] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [selectedInvite, setSelectedInvite] =
    React.useState<InviteWithRelations | null>(null);
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [activeActionInviteId, setActiveActionInviteId] =
    React.useState<string | null>(null);

  const filteredData = React.useMemo(() => {
    if (!data) {
      return [];
    }

    return data.filter((invite) => {
      const searchTerm = search.trim().toLowerCase();
      if (searchTerm) {
        const searchableValues = [
          invite.visitorName,
          invite.visitorEmail,
          invite.visitorWhatsApp,
          invite.purpose,
          invite.accessCode,
          invite.visitorType,
        ];
        if (
          !searchableValues.some((value) =>
            value?.toLowerCase().includes(searchTerm),
          )
        ) {
          return false;
        }
      }

      if (
        visitorTypeFilter !== 'all' &&
        invite.visitorType !== visitorTypeFilter
      ) {
        return false;
      }

      if (checkedInOnly && !invite.scanned) {
        return false;
      }

      if (revokedOnly && !invite.revoked) {
        return false;
      }

      if (startDateFilter || endDateFilter) {
        const filterDate = getFilterDate(invite);
        if (!filterDate || Number.isNaN(filterDate.getTime())) {
          return false;
        }

        if (startDateFilter) {
          const start = new Date(`${startDateFilter}T00:00:00`);
          if (filterDate < start) {
            return false;
          }
        }

        if (endDateFilter) {
          const end = new Date(`${endDateFilter}T23:59:59`);
          if (filterDate > end) {
            return false;
          }
        }
      }

      return true;
    });
  }, [
    data,
    search,
    visitorTypeFilter,
    checkedInOnly,
    revokedOnly,
    startDateFilter,
    endDateFilter,
  ]);

  const handleResend = async (invite: InviteWithRelations) => {
    if (!invite.id || invite.revoked) return;

    setActionMessage(null);
    setActionError(null);
    setActiveActionInviteId(invite.id);
    try {
      const result = await resendInvite({id: invite.id}).unwrap();
      setActionMessage(
        result.message || `Invite resent to ${invite.visitorName}.`,
      );
      await refetch();
    } catch (resendError) {
      console.error('Failed to resend invite:', resendError);
      setActionError(
        'Could not resend this invite. Check that it has an email or WhatsApp number, then try again.',
      );
    } finally {
      setActiveActionInviteId(null);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!selectedInvite?.id || selectedInvite.revoked) return;

    setActionMessage(null);
    setActionError(null);
    setActiveActionInviteId(selectedInvite.id);
    try {
      await revokeInvite({id: selectedInvite.id}).unwrap();
      setActionMessage(`Invite for ${selectedInvite.visitorName} was revoked.`);
      setSelectedInvite(null);
      await refetch();
    } catch (revokeError) {
      console.error('Failed to revoke invite:', revokeError);
      setActionError('Could not revoke this invite. Please try again.');
    } finally {
      setActiveActionInviteId(null);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load invites and visits for this property.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{mb: 1}}>
        Property Invites and Visits
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
        Track visit details and manage passes issued for this property.
      </Typography>

      {actionMessage && (
        <Alert severity="success" sx={{mb: 2}}>
          {actionMessage}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{mb: 2}}>
          {actionError}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{border: '1px solid #eaeaea', borderRadius: 2, p: 2, mb: 2}}
      >
        <Stack
          direction={{xs: 'column', sm: 'row'}}
          spacing={2}
          alignItems={{sm: 'center'}}
          sx={{mb: 2}}
        >
          <TextField
            label="Search invites"
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, contact, purpose, access code"
            fullWidth
          />
          <Button
            startIcon={
              isFetching ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {xs: '1fr', md: '1fr 1fr 1fr'},
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="visitor-type-filter-label">Visitor Type</InputLabel>
            <Select
              labelId="visitor-type-filter-label"
              label="Visitor Type"
              value={visitorTypeFilter}
              onChange={(event) => setVisitorTypeFilter(event.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="bnb">BnB</MenuItem>
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="delivery">Delivery</MenuItem>
              <MenuItem value="visitor">Visitor</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDateFilter}
            onChange={(event) => setStartDateFilter(event.target.value)}
            InputLabelProps={{shrink: true}}
            fullWidth
          />

          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDateFilter}
            onChange={(event) => setEndDateFilter(event.target.value)}
            InputLabelProps={{shrink: true}}
            fullWidth
          />
        </Box>

        <Box sx={{display: 'flex', flexWrap: 'wrap', mt: 1}}>
          <FormControlLabel
            control={
              <Switch
                checked={checkedInOnly}
                onChange={(event) => setCheckedInOnly(event.target.checked)}
              />
            }
            label="Checked-in only"
          />
          <FormControlLabel
            control={
              <Switch
                checked={revokedOnly}
                onChange={(event) => setRevokedOnly(event.target.checked)}
              />
            }
            label="Revoked only"
          />
        </Box>
      </Paper>

      {(!data || data.length === 0) && (
        <Alert severity="info" sx={{mb: 2}}>
          No invites or visits found for this property yet.
        </Alert>
      )}

      {data && data.length > 0 && filteredData.length === 0 && (
        <Alert severity="info" sx={{mb: 2}}>
          No invites match the selected filters.
        </Alert>
      )}

      {filteredData.length > 0 && (
        <Paper elevation={0} sx={{border: '1px solid #eaeaea', borderRadius: 2}}>
          <List>
            {filteredData.map((invite, index) => {
              const scannedAt = invite.scannedAt
                ? new Date(invite.scannedAt).toLocaleString('en-GB')
                : 'Not checked in';
              const visitorType = invite.visitorType
                ? invite.visitorType.toUpperCase()
                : 'VISITOR';

              return (
                <React.Fragment key={invite.id ?? `${invite.visitorName}-${index}`}>
                  <ListItem alignItems="flex-start" sx={{display: 'block', py: 2}}>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography variant="subtitle1">
                            {invite.visitorName}
                          </Typography>
                          <Chip size="small" label={visitorType} color="primary" />
                          {invite.scanned && (
                            <Chip size="small" label="CHECKED IN" color="success" />
                          )}
                          {invite.revoked && (
                            <Chip size="small" label="REVOKED" color="error" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{mt: 1}}>
                          <Typography variant="body2" color="text.secondary">
                            Purpose: {invite.purpose || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Visit Window: {formatVisitWindow(invite)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Last Check-In: {scannedAt}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Access Code: {invite.accessCode || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Contact: {[invite.visitorEmail, invite.visitorWhatsApp]
                              .filter(Boolean)
                              .join(' / ') || 'N/A'}
                          </Typography>
                        </Box>
                      }
                    />

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{mt: 1}}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        component="a"
                        href={
                          invite.id
                            ? `${config.CDN_URL}/invites/${invite.id}.png`
                            : undefined
                        }
                        target="_blank"
                        rel="noreferrer"
                        download
                        disabled={!invite.id}
                      >
                        Download Pass
                      </Button>
                      {!invite.revoked && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            activeActionInviteId === invite.id && isResending ? (
                              <CircularProgress size={14} />
                            ) : (
                              <SendIcon />
                            )
                          }
                          onClick={() => handleResend(invite)}
                          disabled={!invite.id || activeActionInviteId === invite.id}
                        >
                          Resend
                        </Button>
                      )}
                      {!invite.revoked && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={
                            activeActionInviteId === invite.id && isRevoking ? (
                              <CircularProgress size={14} />
                            ) : (
                              <BlockIcon />
                            )
                          }
                          onClick={() => setSelectedInvite(invite)}
                          disabled={!invite.id || activeActionInviteId === invite.id}
                        >
                          Revoke
                        </Button>
                      )}
                    </Stack>
                  </ListItem>
                  {index < filteredData.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}

      <Dialog
        open={Boolean(selectedInvite)}
        onClose={() => !isRevoking && setSelectedInvite(null)}
      >
        <DialogTitle>Revoke invite?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revoke the invite for {selectedInvite?.visitorName}? They will no
            longer be able to use this pass for entry.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelectedInvite(null)}
            disabled={isRevoking}
          >
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleConfirmRevoke}
            disabled={isRevoking}
            startIcon={
              isRevoking ? <CircularProgress size={16} /> : <BlockIcon />
            }
          >
            Revoke Invite
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisitsTabContent;
